import { KindeProvider } from "@kinde-oss/kinde-auth-react";
import { LocalStorage, MemoryStorage, StorageKeys } from "@kinde/js-utils";

import { ReactNode, useContext, useRef } from "react";
import { KindeRedirectContext } from "./KindeRedirectContext";
import { KINDE_ENVIRONMENT } from "./kindeEnvironment";

// Per-origin token store. We pass an explicit LocalStorage store rather than the
// Kinde v5 default, which (in 5.12.0) is an in-memory, per-tab store that
// persists nothing across reloads - so every load re-runs the silent refresh
// against the shared `auth.inrange.dev` / `auth.inrange.io` Kinde tenant. That
// tenant keeps a single browser refresh-token cookie bound to whichever client
// last authenticated, so an app refreshing off it can be handed a token minted
// by another inrange.io / .dev client (admin / customer / agents) - which BMA
// then rejects (a customer token fails is_admin_app_user -> 404; an agents token
// fails the inrange-api audience -> 401). A persistent per-origin LocalStorage
// store avoids this two ways: each origin holds only *its own* client's token,
// and it refreshes off that stored token instead of the shared cookie, so it
// stays off the shared-cookie path in steady state. SSO is unaffected: it rides
// Kinde's own session cookie on the auth domain, so an admin clicking through to
// the customer app silently re-auths with no prompt. Our e2e already proves this
// cross-origin path (localhost:3000 -> :3001, which likewise can't share
// storage). Defined at module scope so the provider gets a stable instance
// across renders.
//
// Tradeoff: a JS-readable store keeps the refresh token in localStorage (the
// exposure `useInsecureForRefreshToken` is named for) rather than an HttpOnly
// cookie. Accepted deliberately - see docs/kinde-v5-foreign-token-healing.md.
//
// Fall back to in-memory storage when localStorage is unavailable (Safari
// private browsing, storage disabled, sandboxed context). The SDK's
// LocalStorage store calls `localStorage.getItem` with no guard, so on init it
// throws "null is not an object (evaluating 'localStorage.getItem')" and crashes
// the whole app; MemoryStorage degrades gracefully (tokens just don't persist
// across reloads - the Kinde session silently re-auths each load).
const localStorageUsable = (): boolean => {
  try {
    const probe = "__kinde_ls_probe__";
    window.localStorage.setItem(probe, "1");
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
};
const kindeTokenStore = localStorageUsable()
  ? new LocalStorage()
  : new MemoryStorage();

// Kinde's LocalStorage store prefixes every key with "kinde-".
const KINDE_LOCALSTORAGE_PREFIX = "kinde-";

const clearKindeLocalStorage = (): void => {
  Object.keys(window.localStorage)
    .filter((k) => k.startsWith(KINDE_LOCALSTORAGE_PREFIX))
    .forEach((k) => window.localStorage.removeItem(k));
};

// One-time cleanup of a foreign-client token left in this origin's localStorage.
// Before per-origin isolation (above) the access/id token lived in the shared
// .inrange.io cookie, so a token minted by another client (customer / agents)
// could be sitting in the cookie when isolation shipped - and the SDK migrated
// it into this origin's localStorage on first load. The SDK then keeps sending
// that token to BMA forever (it never checks which client minted it), and the
// #1139 validity gate (correctly) rejects it -> a "Session expired" modal that
// never clears. If the stored access token's `azp` isn't ours, clear the Kinde
// store so the SDK re-authenticates for our own client on init. A no-op once
// each user has migrated, and best-effort so it can never block app start.
//
// NB: we must clear window.localStorage directly, NOT via the store's
// destroySession() - that only removes keys this *instance* wrote during this
// page load (its `internalItems` Set), which is empty on a fresh load, so it
// leaves a token persisted by a previous load in place (the exact case here).
const purgeForeignClientToken = (clientId: string): void => {
  try {
    const token = kindeTokenStore.getSessionItem(StorageKeys.accessToken);
    if (typeof token !== "string" || !token) return;
    const payload = token.split(".")[1];
    if (!payload) return;
    const claims = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    if (typeof claims.azp === "string" && claims.azp !== clientId) {
      clearKindeLocalStorage();
    }
  } catch {
    // Storage/parse error - leave the store untouched rather than risk wiping a
    // valid session; the normal auth flow still handles a genuinely bad token.
  }
};

// Wrapper component needed to allow for use of the KindeRedirectContext for storing the redirect URL
const KindeAuthenticatedUI = ({
  kindeClientId,
  children,
  useInsecureForRefreshToken,
}: {
  kindeClientId: string;
  children: ReactNode;
  useInsecureForRefreshToken?: boolean;
}) => {
  const { setRedirectUrl } = useContext(KindeRedirectContext);

  // Run once, synchronously on first render, so it happens before KindeProvider
  // (our child) initialises from the store below.
  const purgedRef = useRef(false);
  if (!purgedRef.current) {
    purgedRef.current = true;
    purgeForeignClientToken(kindeClientId);
  }

  return (
    <KindeProvider
      clientId={kindeClientId}
      domain={`https://${KINDE_ENVIRONMENT.KINDE_AUTH_DOMAIN}`}
      redirectUri={`${document.location.protocol}//${document.location.host}/login-redirect`}
      logoutUri={`${document.location.protocol}//${document.location.host}`}
      audience="inrange-api"
      // Kinde v5 made token refresh opt-in: getToken()/getAccessToken() are
      // plain reads of the cached access token and never refresh, and the
      // on-focus silent refresh is gated on this prop (defaults to false). The
      // v3 SDK refreshed automatically, so without this an access token that
      // expires while a tab is open is never renewed and every BMA request
      // 401s until a full reload re-mints it. This restores renew-on-focus;
      // getAuthTokenBlocking adds the per-request guard for foreground tabs.
      refreshOnFocus={true}
      store={kindeTokenStore}
      callbacks={{
        onSuccess: (_user, state) => {
          if (state["redirectTo"]) {
            setRedirectUrl!(state["redirectTo"] as string);
          }
        },
        onError: (_error, state) => {
          if (state["redirectTo"]) {
            setRedirectUrl!(state["redirectTo"] as string);
          }
        },
      }}
      useInsecureForRefreshToken={!!useInsecureForRefreshToken}
    >
      {children}
    </KindeProvider>
  );
};

export default KindeAuthenticatedUI;
